import Store from '../store';
import priceString from '@utils/price-string';
import { get as getProp } from 'lodash';
import cartService from '@api/routes/cart';
import cartAddLineService from '@api/routes/cart/addline';
import cartAddMultipleLineService from '@api/routes/cart/addMultipleline';
import cartSubmitService from '@api/routes/cart/submit';
import checkoutService from '@api/routes/shop/checkout/cart';
import removeLinesService from '@api/routes/cart/removeline';
import removePromoService from '@api/routes/cart/removepromotion';
import updateLineService from '@api/routes/cart/updateline';
import validatePromoService from '@api/routes/cart/validateandaddpromotion';
import { miniCartToggle } from '../../components/mini-cart/mini-cart-toggle';
import * as cartDatalayer from '@datalayer/components/cart-datalayer';
import * as productDatalayer from '@datalayer/components/product-datalayer';

/** 
 * @typedef {Object} StoreContext
 * @property {Function} commit
 * @property {Function} dispatch
 * @property {Object<string,any>} getters
 * @property {Object<string,any>} state
 */

/** 
 * @typedef {Object} AddlineOptions
 * @property {number} autoshipFrequency
 * @property {string} catalogName
 * @property {number} exceptionAmount
 * @property {string} exceptionBehavior
 * @property {number} quantity
 * @property {boolean} IsRefill
 */

/** 
 * @typedef {Object} Attribute
 * @property {string} label
 * @property {string} value
 */

/**
 * map address object to formatted string
 * @param {Object<string,any>} address 
 * @returns {string}
 */
const formatAddress = (address) => {
    let text = '';
    if(address) {
        text = address.Address1;
        if (address.Address2) {
            text += ` ${address.Address2}`;
        }
        text += ` ${address.City}, ${address.State} ${address.ZipPostalCode}`;
    }

    return text;
};

const EXCEPTION_BEHAVIOR_VALUES = {
    NO_EXCEPTION: -1,
    STRIKE_THROUGH: 0,
    REPLACE: 1
};

const DEFAULT_PENDING_UPDATE = {
    lineId: null,
    quantity: null,
    autoshipFrequency: null,
    petId: null
};

const DEFAULT_ADDLINE_OPTIONS = {
    AutoshipFrequency: 0,
    CatalogName: 'VCA_Master',
    ExceptionAmount: 0,
    ExceptionBehavior: null,
    Quantity: 1,
    IsRefill: false
};

const store = new Store({
    state: {
        transactionId: '',
        billingParty: null,
        cachedLineIds: [],
        cachedMappedLines: [],
        currencyCode: '',
        discount: '',
        error: '',
        freeShippingText: '',
        hospitalId: '',
        isReview: false,
        loaded: false,
        loading: false,
        loadingLine: null,
        miniCartTotalFormatted: '',
        paymentMethod: null,
        pendingUpdate: DEFAULT_PENDING_UPDATE,
        petSelectionValidated: false,
        promoCode: '',
        rawLines: [],
        shippingParty: null,
        shippingMethod: null,
        shippingTotal: 0,
        shippingTotalFormatted: '',
        subtotal: 0,
        subtotalFormatted: '',
        taxTotal: 0,
        taxTotalFormatted: '',
        total: 0,
        totalFormatted: ''
    },
    actions: {
        async getCart({ commit, dispatch, state }) {
            if(state.loading) {
                return { cancelled: true };
            }

            commit('SET_LOADING');
            commit('SET_ERROR', '');

            const { success, data, error } = await cartService.get();

            if(success) {
                dispatch('setCartData', data)
            } else {
                commit('SET_ERROR', error || `Something unexpected happened and we couldn't load your cart. Please try again later.`);

                commit('SET_FORMATTED_SUMMARY', {
                    miniCartTotal: 'n/a',
                    discount: 'n/a',
                    shippingTotal: 'n/a',
                    subtotal: 'n/a',
                    taxTotal: 'n/a',
                    total: 'n/a'
                });
            }

            commit('SET_LOADING', false);
            commit('SET_LOADED', true);

            return { success, error };
        },
        async submitCart() {
            const response = await cartSubmitService.get();
          
            if(response.success) {
                if(response.data.RedirectUrl) {
                    // slight delay before redirecting to process dataLayer push
                    setTimeout(() => window.location.href = response.data.RedirectUrl, 500);
                } else {
                   console.error('Cart submitted but no redirect returned. A property named "RedirectUrl" is expected in the response.', response);
                }
            } else {
                response.error = response.error ? response.error : `Something unexpected happened and we couldn't place your order. Please try again later.`;
            }
            
            return response;
        },
        /**
         * @param {StoreContext} context store context 
         * @param {string} productId 
         * @param {string} variantId 
         * @param {AddlineOptions} options 
         * @returns 
         */
        async addLine({ commit, dispatch, getters, state }, productId, variantId, options = {}, petId) {
            const payload = {
                ProductId: productId,
                VariantId: variantId,
                AutoshipFrequency: options.autoshipFrequency || DEFAULT_ADDLINE_OPTIONS.AutoshipFrequency,
                CatalogName: options.catalogName || DEFAULT_ADDLINE_OPTIONS.CatalogName,
                ExceptionAmount: options.exceptionAmount || DEFAULT_ADDLINE_OPTIONS.ExceptionAmount,
                ExceptionBehavior: options.exceptionBehavior ?? DEFAULT_ADDLINE_OPTIONS.ExceptionBehavior,
                Quantity: options.quantity || DEFAULT_ADDLINE_OPTIONS.Quantity,
                PetId: petId,
                IsRefill: options.IsRefill || DEFAULT_ADDLINE_OPTIONS.IsRefill
            };

            commit('SET_LOADING_LINE', '_newLine_');

            const response = await cartAddLineService.post(payload);

            if(response.success) {
                dispatch('setCartData', response.data);

                const addedLineIndex = getters.lines.findIndex((line) => line.productId === productId && line.variantId === line.variantId);
                cartDatalayer.sendAddToCart(state.currencyCode, getters.linesGAProducts[addedLineIndex]);
            }

            commit('SET_LOADING_LINE', null);
            commit('SET_LOADED', true);

            return response;
        },
        async addMultipleLines({ commit, dispatch, getters, state }, products) {
            const payload = products;

            commit('SET_LOADING_LINE', '_newLine_');

            const response = await cartAddMultipleLineService.post(payload);

            if(response.success) {
                dispatch('setCartData', response.data);

                const addedLineIndex = getters.lines.findIndex((line) => line.productId === productId && line.variantId === line.variantId);
                cartDatalayer.sendAddToCart(state.currencyCode, getters.linesGAProducts[addedLineIndex]);
            }

            commit('SET_LOADING_LINE', null);
            commit('SET_LOADED', true);

            return response;
        },
        async removeLine({ commit, dispatch, getters, state }, lineId) {
            commit('SET_LOADING_LINE', lineId);

            const { success, data, error } = await removeLinesService.post({
                LineIds: [ lineId ]
            });

            if(success) {
                const removedLineIndex = getters.lines.findIndex((line) => line.lineId === lineId);
                cartDatalayer.sendRemoveFromCart(state.currencyCode, getters.linesGAProducts[removedLineIndex]);

                dispatch('setCartData', data);
            } else {
                console.error(error);
            }

            if(lineId = state.pendingUpdate.lineId) {
                commit('RESET_PENDING_UPDATE');
            }

            commit('SET_LOADING_LINE', null);

            return { success, error };
        },
        async updateLine({ commit, dispatch, state }) {
            commit('SET_LOADING_LINE', state.pendingUpdate.lineId);

            const updateLinePayload = {
                CartLineId: state.pendingUpdate.lineId,
                AutoshipFrequency: state.pendingUpdate.autoshipFrequency,
                PetId: state.pendingUpdate.petId,
                Quantity: state.pendingUpdate.quantity
            };

            const { success, data, error } = await updateLineService.post(updateLinePayload);
            
            if(success) {
                dispatch('setCartData', data);
                commit('RESET_PENDING_UPDATE');
            } else {
                console.error(error);
            }

            commit('SET_LOADING_LINE', null);

            return { success, error };
        },
        async applyPromo({ commit, dispatch }, promoCode) {
            commit('SET_LOADING');

            const response = await validatePromoService.post({
                PromoCode: promoCode
            });

            if(response.success && response.data?.Valid) {
                cartDatalayer.sendApplyPromo(response.data.PromoCode);

                commit('SET_PROMO_CODE', response.data.PromoCode);

                dispatch('setFormattedCartSummary', {
                    miniCartTotal: response.data?.FormattedTotal?.FormattedMiniCartTotal,
                    discount: response.data?.FormattedTotal?.FormattedDiscount,
                    shippingTotal: response.data?.FormattedTotal?.FormattedShipping,
                    subtotal: response.data?.FormattedTotal?.FormattedSubtotal,
                    taxTotal: response.data?.FormattedTotal?.FormattedTax,
                    total: response.data?.FormattedTotal?.FormattedGrandTotal
                });
            }

            commit('SET_LOADING', false);

            return response;
        },
        async removePromo({ commit, dispatch }) {
            commit('SET_LOADING');

            const response = await removePromoService.get();

            if(response.success) {
                commit('SET_PROMO_CODE', '');

                dispatch('setFormattedCartSummary', {
                    miniCartTotal: response.data?.FormattedTotal?.FormattedMiniCartTotal,
                    discount: response.data?.FormattedTotal?.FormattedDiscount,
                    shippingTotal: response.data?.FormattedTotal?.FormattedShipping,
                    subtotal: response.data?.FormattedTotal?.FormattedSubtotal,
                    taxTotal: response.data?.FormattedTotal?.FormattedTax,
                    total: response.data?.FormattedTotal?.FormattedGrandTotal
                });
            }

            commit('SET_LOADING', false);

            return response;
        },
        setCartData({ commit, dispatch, getters }, data) {
            dispatch('setCartSummary', {
                discount: data?.FormattedTotal?.FormattedDiscount,
                shippingTotal: data?.Total?.ShippingTotal,
                subtotal: data?.Total?.Subtotal,
                taxTotal: data?.Total?.TaxTotal?.Amount,
                total: data?.Total?.Amount
            });

            dispatch('setFormattedCartSummary', {
                miniCartTotal: data?.FormattedTotal?.FormattedMiniCartTotal,
                discount: data?.FormattedTotal?.FormattedDiscount,
                shippingTotal: data?.FormattedTotal?.FormattedShipping,
                subtotal: data?.FormattedTotal?.FormattedSubtotal,
                taxTotal: data?.FormattedTotal?.FormattedTax,
                total: data?.FormattedTotal?.FormattedGrandTotal
            });

            commit('SET_TRANSACTION_ID', data.EntityOrderId);
            commit('SET_BILLING_PARTY', data.BillingParty);
            commit('SET_CURRENCY_CODE', data.CurrencyCode);
            commit('SET_FREE_SHIPPING_TEXT', data.FreeShippingText);
            commit('SET_HOSPITAL_ID', data.HospitalId);
            commit('SET_PAYMENT_METHOD', data.PaymentMethod);
            commit('SET_PROMO_CODE', data.PromotionCode);
            commit('SET_SHIPPING_PARTY', data.ShippingParty);
            commit('SET_SHIPPING_METHOD', data.Shipping);
            commit('SET_CACHED_LINES', { lineIds: getters.lineIds, mappedLines: getters.lines });
            commit('SET_RAW_LINES', data.Lines);
        },
        /** set cart summary as numbers */
        setCartSummary({ commit }, { discount, shippingTotal, subtotal, taxTotal, total }) {
            commit('SET_DISCOUNT', discount);

            commit('SET_SUMMARY', {
                shippingTotal,
                subtotal,
                taxTotal,
                total
            });
        },
        /** set cart summary as formatted strings */
        setFormattedCartSummary({ commit }, {miniCartTotal, discount, shippingTotal, subtotal, taxTotal, total }) {
            commit('SET_FORMATTED_SUMMARY', {
                miniCartTotal,
                discount,
                shippingTotal,
                subtotal,
                taxTotal,
                total
            });
        },
        focusMiniCart(context, productInfo) {
            miniCartToggle?.focus(productInfo);
        }
    },
    getters: {
        billingPartyId(state) {
            return state.billingParty?.AddressName || null;
        },
        billingPartyCity(state) {
            return state.billingParty?.City || null;
        },
        billingPartyState(state) {
            return state.billingParty?.State || null;
        },
        billingPartyAddress1(state) {
            return state.billingParty?.Address1 || null;
        },
        billingPartyAddress2(state) {
            return state.billingParty?.Address2 || null;
        },
        billingPartyZipPostalCode(state) {
            return state.billingParty?.ZipPostalCode || null;
        },
        billingPartyLastName(state) {
            return state.billingParty?.LastName || null;
        },
        billingPartyFirstName(state) {
            return state.billingParty?.FirstName || null;
        },
        cardPreview(state) {
            if(!state.paymentMethod) {
                return null;
            }

            return `${state.paymentMethod.CardType} ****${state.paymentMethod.LastDigits}`;
        },
        formattedBillingAddress(state) {
            return formatAddress(state.billingParty);
        },
        formattedDiscount(state) {
            return state.discount || priceString(0);
        },
        formattedMiniCartTotal(state) {
            return state.miniCartTotalFormatted;
        },
        formattedShippingAddress(state) {
            return formatAddress(state.shippingParty);
        },
        formattedShippingTotal(state) {
            return state.shippingTotalFormatted ||  priceString(state.shippingTotal);
        },
        formattedSubtotal(state) {            
            return state.subtotalFormatted ||  priceString(state.subtotal);
        },
        formattedTaxTotal(state) {            
            return state.taxTotalFormatted || priceString(state.taxTotal);
        },
        formattedTotal(state) {
            return state.totalFormatted || priceString(state.total);
        },
        hasPendingPet(state, getters) {
            return !!getters.pendingPetLineIds?.length || false;
        },
        isBackOrdered(state, getters){
            return !!getters.pendingRemoveBackorder?.length || false;
        },
        hasPendingUpdate(state) {
            return state.pendingUpdate.lineId !== null;
        },
        isEmpty(state) {
            return state.rawLines.length === 0;
        },
        isPetInvalid: (state, getters) => (lineId) => {
            return state.petSelectionValidated && getters.pendingPetLineIds.includes(lineId);
        },
        isLineUpdateDisabled: (state) => (lineId) => {
            if(!state.pendingUpdate.lineId) {
                return false;
            }

            return state.pendingUpdate.lineId !== lineId;
        },
        line: (state, getters) => (lineId) => {
            return getters.lines.find((line) => line.lineId === lineId);
        },
        /**
         * array of attribute values and labels
         * @param {string} lineId id of line to get attributes from
         * @param {Object<string, string>} labels object of label strings to. property name(s) should match the property name of attribute value
         * @returns {Array<Attribute>}
         */
        lineAttributes: (state, getters) => (lineId, labels) => {
            const line = getters.line(lineId);
            if(!line) return [];

            const {
                flavor: flavorLabel,
                packaging: packagingLabel,
                strengthSize: strengthSizeLabel,
                strengthSizeSupplies: strengthSizeSuppliesLabel,
                rxForm: rxFormLabel
            } = labels;

            const attrArr = [
                { label: flavorLabel, value: line.attributes.flavor },
                { label: packagingLabel, value: line.attributes.packaging },
                { label: strengthSizeLabel, value: line.attributes.strengthSize },
                { label: strengthSizeSuppliesLabel, value: line.attributes.strengthSizeSupplies },
                { label: rxFormLabel, value: line.attributes.rxForm }
            ];

            return attrArr.filter(({ value }) => value);
        },
        /**
         * array of lineIds used to help define line order
         * @returns {Array<string>}
         */
        lineIds(state) {
            return state.rawLines.map(({ LineId }) => LineId);
        },
        /**
         * array of normalized line data
         * @returns {Array<Object<string,any>>}
         */
        lines(state) {
            return state.rawLines.map(line => {
                return {
                    attributes: {
                        flavor: getProp(line, 'Variant.Flavor', null),
                        packaging: getProp(line, 'Variant.Packaging', null),
                        strengthSize: getProp(line, 'Variant.StrengthSize', null),
                        strengthSizeSupplies: getProp(line, 'Variant.StrengthSizeSupplies', null),
                        rxForm: getProp(line, 'Variant.RxForm', null)
                    },
                    autoshipFrequency: line.AutoshipFrequency,
                    brand: getProp(line, 'Product.Brand', ''),
                    category: getProp(line, 'Product.ProductCategory'),
                    displayName: getProp(line, 'Product.DisplayName', ''),
                    name: getProp(line, 'Product.Name', ''),
                    exceptionAmount: line.ExceptionAmount,
                    exceptionBehavior: line.ExceptionBehavior,
                    image: {
                        alt: getProp(line, 'Variant.Image.Alt', null),
                        constrainedUrl: getProp(line, 'Variant.Image.ConstrainedUrl', null)
                    },
                    lineId: line.LineId,
                    listPrice: getProp(line, 'Product.Price.ListPrice', 0),
                    petId: line.PetId,
                    productId: getProp(line, 'Product.ProductId', ''),
                    productUrl: line.ProductUrl,
                    quantity: line.Quantity,
                    rxRequired: getProp(line, 'Variant.RxRequired', false),
                    variantId: getProp(line, 'Product.ProductVariantId', ''),
                    backOrdered: line.BackOrdered
                }
            });
        },
        /**
         * array of lines formatted for google analytics
         * @returns {Array<Object<string,any>>}
         */
        linesGAProducts(state, getters) {
            return getters.lines.map((line) => productDatalayer.buildProduct({
                id: line.productId,
                variantId: line.variantId,
                name: line.name,
                brand: line.brand, 
                category: line.category,
                price: line.listPrice,
                quantity: line.quantity,
                autoshipFrequency: line.autoshipFrequency,
                pet: line.petId,
                discount: line.exceptionBehavior !== EXCEPTION_BEHAVIOR_VALUES.NO_EXCEPTION,
                rxRequired: line.rxRequired,
                strength: line.attributes.strengthSize,
                packaging: line.attributes.packaging
            }));
        },
        linesAdded(state, getters) {
            return getters.lineIds
                    .filter((id) => !state.cachedLineIds.includes(id));
        },
        linesCount(state, getters) {
            return getters.lines.map(line => line.quantity).reduce((prev, current) => prev + current, 0);
        },
        linesRemoved(state, getters) {
            return state.cachedLineIds
                    .filter(id => !getters.lineIds.includes(id));
        },
        paymentMethodId(state) {
            return state.paymentMethod?.Id || null;
        },
        pendingUpdateLineId(state) {
            return state.pendingUpdate.lineId;
        },
        pendingPetLineIds(state, getters) {
            return getters.lines
                    .filter(({ petId }) => !petId)
                    ?.map(({ lineId }) => lineId) || [];;
        },
        pendingRemoveBackorder(state, getters) {
            return getters.lines
                    .filter(({ backOrdered }) => backOrdered == true)
                    ?.map(({ petId }) => petId) || [];;
        },
        shippingPartyId(state) {
            return state.shippingParty?.AddressName || null;
        },
        shippingMethodId(state) {
            return state.shippingMethod?.ShippingMethodId || null;
        }
    },
    mutations: {
        RESET_PENDING_UPDATE(state) {
            state.pendingUpdate = DEFAULT_PENDING_UPDATE;
        },
        SET_TRANSACTION_ID(state, transactionId) {
            state.transactionId = transactionId;
        },
        SET_BILLING_PARTY(state, billingParty) {
            state.billingParty = billingParty || null;
        },
        SET_CACHED_LINES(state, { lineIds, mappedLines }) {
            state.cachedLineIds = lineIds;
            state.cachedMappedLines = mappedLines;
        },
        SET_CURRENCY_CODE(state, currencyCode) {
            state.currencyCode = currencyCode;
        },
        SET_DISCOUNT(state, discount) {
            state.discount = discount;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_FREE_SHIPPING_TEXT(state, freeShippingText) {
            state.freeShippingText = freeShippingText;
        },
        SET_FORMATTED_SUMMARY(state, { miniCartTotal, discount, shippingTotal, subtotal, taxTotal, total }) {
            state.miniCartTotalFormatted = miniCartTotal || '';
            state.discount = discount || '';
            state.shippingTotalFormatted = shippingTotal || '';
            state.subtotalFormatted = subtotal || '';
            state.taxTotalFormatted = taxTotal || '';
            state.totalFormatted = total || '';
        },
        SET_HOSPITAL_ID(state, hospitalId) {
            state.hospitalId = hospitalId;
        },
        SET_IS_REVIEW(state, isReview) {
            state.isReview = isReview;
        },
        SET_LOADED(state, loaded) {
            state.loaded = loaded;
        },
        SET_LOADING(state, loading = true) {
            state.loading = loading;
        },
        SET_LOADING_LINE(state, lineId) {
            state.loadingLine = lineId;
        },
        SET_PAYMENT_METHOD(state, paymentMethod) {
            state.paymentMethod = paymentMethod || null;
        },
        SET_PENDING_UPDATE(state, pendingUpdate = {}) {
            if(!pendingUpdate.lineId) {
                return;
            }

            state.pendingUpdate = pendingUpdate;
        },
        SET_PET_SELECTION_VALIDATED(state, validated) {
            state.petSelectionValidated = validated;
        },
        SET_PROMO_CODE(state, promoCode) {
            state.promoCode = promoCode;
        },
        SET_RAW_LINES(state, lines = []) {
            state.rawLines = lines || [];
        },
        SET_SHIPPING_PARTY(state, shippingParty) {
            state.shippingParty = shippingParty || null;
        },
        SET_SHIPPING_METHOD(state, shippingMethod) {
            state.shippingMethod = shippingMethod || null;
        },
        SET_SHIPPING_TOTAL(state, shippingTotal) {
            state.shippingTotal = shippingTotal || 0;
        },
        SET_SUMMARY(state, { shippingTotal, subtotal, taxTotal, total }) {
            state.shippingTotal = shippingTotal || 0;
            state.subtotal = subtotal || 0;
            state.taxTotal = taxTotal || 0;
            state.total = total || 0;
        },
        SET_POST_ACTION_URL(state, url) {
            checkoutService.setRoute(url);
        }
    }
});

export {
    EXCEPTION_BEHAVIOR_VALUES as exceptionBehaviorValues
};

export const {
    actions,
    mutations,
    state,
    watch
} = store;

export default store;
